import React, { useState } from "react";
import { Ruler, Shirt, Info } from "lucide-react";

// All base measurements in inches. cm derived on the fly.
const CHARTS = {
  "Men's Tops": {
    cols: ["Size", "Chest", "Length", "Shoulder"],
    rows: [
      ["S", 38, 27, 17], ["M", 40, 28, 17.5], ["L", 42, 29, 18],
      ["XL", 44, 30, 18.5], ["XXL", 46, 31, 19],
    ],
  },
  "Men's Bottoms": {
    cols: ["Size", "Waist", "Hip", "Inseam"],
    rows: [
      ["28", 28, 36, 30], ["30", 30, 38, 30.5], ["32", 32, 40, 31],
      ["34", 34, 42, 31.5], ["36", 36, 44, 32], ["38", 38, 46, 32],
    ],
  },
  "Women's Tops": {
    cols: ["Size", "Bust", "Waist", "Length"],
    rows: [
      ["XS", 32, 26, 23], ["S", 34, 28, 23.5], ["M", 36, 30, 24],
      ["L", 38, 32, 24.5], ["XL", 40, 34, 25], ["XXL", 42, 36, 25.5],
    ],
  },
  "Women's Bottoms": {
    cols: ["Size", "Waist", "Hip", "Inseam"],
    rows: [
      ["26", 26, 35, 28], ["28", 28, 37, 28.5], ["30", 30, 39, 29],
      ["32", 32, 41, 29.5], ["34", 34, 43, 30],
    ],
  },
  "Kids (by age)": {
    cols: ["Age", "Height", "Chest", "Waist"],
    rows: [
      ["2-3Y", 36, 21, 20], ["4-5Y", 42, 23, 21], ["6-7Y", 48, 25, 22],
      ["8-9Y", 53, 27, 23], ["10-11Y", 57, 29, 24],
    ],
  },
};

const TABS = Object.keys(CHARTS);

export default function SizeGuide() {
  const [unit, setUnit] = useState("in");
  const [tab, setTab] = useState(TABS[0]);
  const chart = CHARTS[tab];
  const fmt = (v, isLabel) => (isLabel ? v : unit === "in" ? `${v}"` : `${Math.round(v * 2.54)} cm`);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="size-guide-page">
      {/* hero */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1D3557] via-[#17284a] to-[#0c1830] text-white p-8 sm:p-10">
        <div className="absolute -top-16 right-0 w-64 h-64 rounded-full bg-[#D4AF37]/20 blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37] mb-3">
            <Ruler size={16} /> Size Guide
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold">Find your perfect fit</h1>
          <p className="text-slate-300 mt-3 max-w-2xl">
            Bull Creations garments follow standard Indian apparel sizing. Measure a well-fitting garment you already own
            and compare with the charts below. When in doubt between two sizes, size up for a relaxed fit.
          </p>
        </div>
      </div>

      {/* controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-6">
        <div className="flex flex-wrap gap-2" data-testid="size-guide-tabs">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`size-tab-${t.replace(/[^a-zA-Z]/g, "-").toLowerCase()}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                tab === t ? "bg-[#E63946] text-white border-[#E63946]" : "border-slate-300 text-slate-600 hover:border-[#E63946]"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="inline-flex rounded-full border p-1 bg-white dark:bg-slate-900 self-start" data-testid="unit-toggle">
          {["in", "cm"].map((u) => (
            <button
              key={u}
              onClick={() => setUnit(u)}
              data-testid={`unit-${u}`}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                unit === u ? "bg-[#1D3557] text-white" : "text-slate-500"
              }`}
            >
              {u === "in" ? "Inches" : "Centimetres"}
            </button>
          ))}
        </div>
      </div>

      {/* table */}
      <div className="mt-5 rounded-2xl border bg-white dark:bg-slate-900 overflow-hidden" data-testid="size-guide-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#1D3557] text-white text-left">
              <tr>
                {chart.cols.map((c) => (
                  <th key={c} className="p-3 font-semibold">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {chart.rows.map((row, ri) => (
                <tr key={ri} className="border-t odd:bg-slate-50/60 dark:odd:bg-slate-800/40">
                  {row.map((cell, ci) => (
                    <td key={ci} className={`p-3 ${ci === 0 ? "font-bold text-[#1D3557] dark:text-[#D4AF37]" : "text-slate-600 dark:text-slate-300"}`}>
                      {fmt(cell, ci === 0)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* how to measure */}
      <div className="grid md:grid-cols-2 gap-5 mt-8">
        <div className="rounded-2xl border bg-white dark:bg-slate-900 p-6">
          <div className="flex items-center gap-2.5 mb-3">
            <Shirt size={18} className="text-[#E63946]" />
            <h3 className="font-display font-bold text-lg">How to measure</h3>
          </div>
          <ul className="space-y-2.5 text-sm text-slate-600 dark:text-slate-300">
            <li><b>Chest / Bust:</b> measure around the fullest part, keeping the tape level under the arms.</li>
            <li><b>Waist:</b> measure around the narrowest part of your natural waistline.</li>
            <li><b>Hip:</b> measure around the fullest part of your hips.</li>
            <li><b>Length:</b> from the highest shoulder point straight down to the hem.</li>
            <li><b>Inseam:</b> from the crotch seam to the bottom of the leg.</li>
          </ul>
        </div>
        <div className="rounded-2xl border border-dashed border-[#D4AF37]/50 bg-[#D4AF37]/5 p-6">
          <div className="flex items-center gap-2.5 mb-3">
            <Info size={18} className="text-[#D4AF37]" />
            <h3 className="font-display font-bold text-lg">Fit tips</h3>
          </div>
          <ul className="space-y-2.5 text-sm text-slate-600 dark:text-slate-300">
            <li>Measurements are of the body, not the garment. We add ease for comfort.</li>
            <li>Regular fit sits true to size; Oversized runs 1 size larger; Slim fit is closer to the body.</li>
            <li>For kids, go by height first, then chest — choose the larger if in between.</li>
            <li>Still unsure? Email <b>care@bullcreations.com</b> and we'll help you pick.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
